/**
 * `F6` · Lo que el panel lee del programa de sellos.
 *
 * Aparte de `mostrador.js` por lo mismo que aquello se separó de
 * `inscripcion.js`: son tres superficies con tres roles y tres riesgos. Aquí
 * está lo que ve **el dueño**, que es lo único que mira el negocio por dentro.
 */
import { z } from 'zod';
/**
 * `M-07` — premios alcanzados y sin canjear, y lo que costarían.
 *
 * El **pasivo contable**: dinero que el dueño ya debe y no ha pagado.
 *
 * Se llama «pasivo» y no «premios pendientes» porque `esquemaPremioPendiente`
 * —singular, en `inscripcion.js`— ya es otra cosa: **un** premio esperando en el
 * mostrador, que es lo que ve el comensal. Dos nombres a un autocompletado de
 * distancia para dos cosas de dos superficies distintas es un error esperando; y
 * «pasivo» es además la palabra con la que [[Metricas]] lo describe.
 *
 * ── Tres números y no uno, y el del medio es el que hace honesto al tercero ──
 * `costoCentavos` sólo suma los premios que **tienen** costo puesto, porque el
 * precio es opcional —una cafetería puede no haberlo escrito nunca—. Un total a
 * secas se leería como la deuda entera, y con la mitad de los premios sin
 * valorar sería la mitad de la deuda con cara de completa. `conCosto` dice
 * sobre cuántos de los `premios` está calculado, y la pantalla lo rotula.
 */
export const esquemaPasivoDePremios = z.object({
    /** Cuántos premios hay vivos: alcanzados, sin canjear y sin vencer. */
    premios: z.number().int().nonnegative(),
    /** De ésos, cuántos traen costo estimado. Nunca mayor que `premios`. */
    conCosto: z.number().int().nonnegative(),
    /** La suma, en centavos enteros. Cero si ninguno trae costo. */
    costoCentavos: z.number().int().nonnegative(),
}).strict();
/**
 * Si el número que se enseña está calculado sobre **todos** los premios.
 *
 * Vive aquí y no en la pantalla porque la frase que lo acompaña —«sobre 8 de
 * 12»— es la diferencia entre un informe y una cifra inventada, y el día que
 * este dato viaje por otro sitio —un correo semanal, el reporte de `R-08`— el
 * criterio tiene que ser el mismo.
 */
export const costoEstaCompleto = (p) => p.premios === p.conCosto;
/**
 * `R-08` — qué pasaría al encender «los sellos valen en todas mis sucursales».
 *
 * Es lo que la pantalla enseña **antes** de confirmar, y la decisión del
 * 2026-09-22 lo puso como condición: «fusionar por defecto, con el costo
 * delante». Lo que lo hace necesario de verdad es que **no se puede deshacer**.
 *
 * `costoCentavos` nulo quiere decir **el programa no tiene precio puesto**, no
 * cero. Un cero delante de un botón irreversible diría «no te cuesta nada».
 */
export const esquemaCostoDeFusionar = z.object({
    /** Cuántas personas alcanzan premio en el momento de encenderlo. */
    clientes: z.number().int().nonnegative(),
    /** Cuántos premios se crean. Una persona puede alcanzar más de uno. */
    premios: z.number().int().nonnegative(),
    costoCentavos: z.number().int().nonnegative().nullable(),
}).strict();
/** Lo que devuelve encenderlo: los premios que se pagaron en el acto. */
export const esquemaFusionHecha = z.object({
    premiosNuevos: z.number().int().nonnegative(),
}).strict();
/**
 * Si encender la fusión no le cuesta nada a nadie **hoy**.
 *
 * No quiere decir que sea gratis: quiere decir que en este momento ninguna
 * tarjeta cruza el umbral. La pantalla cambia de tono —de «esto dispara N
 * premios» a «nadie alcanza premio ahora mismo»— y la advertencia de que no se
 * deshace **se queda igual en los dos casos**, que es lo que importa.
 */
export const fusionarNoDisparaNada = (c) => c.premios === 0;
/* ── `T-62` · El rastro de la caja, mirado — `M-12`, `M-13` y `R-09` ────────
 *
 * Tres listas y no una: son tres preguntas distintas del dueño —«¿quién trabaja
 * y cómo?», «¿qué toqué yo a mano?», «¿qué se entregó sin comprobar?»— y
 * mezclarlas en una tabla obligaría a leerla entera para contestar cualquiera.
 */
/**
 * `M-12` — lo que hizo una persona del mostrador en la ventana pedida.
 *
 * **Sin veredicto.** `fueraDeHorario` es un contraste, no una acusación: dice
 * cuántas de sus operaciones cayeron fuera del horario de su sucursal, y quien
 * lo interpreta es el dueño. Un umbral automático con tres semanas de datos
 * sonaría todos los días, y una alarma que suena siempre se apaga.
 */
export const esquemaActividadDeEmpleado = z.object({
    empleado: z.uuid(),
    nombre: z.string().min(1),
    sellos: z.number().int().nonnegative(),
    canjes: z.number().int().nonnegative(),
    /** `R-09` · De sus canjes, cuántos fueron sin el código del comensal. */
    sinVerificacion: z.number().int().nonnegative(),
    fueraDeHorario: z.number().int().nonnegative(),
    /**
     * La sucursal no tiene horario cargado, así que **no hay con qué contrastar**.
     * Con esto la pantalla dice «sin horario cargado» en vez de enseñar un cero
     * que se lee como «todo en orden» — o, peor, un total que se lee como «todo
     * fuera de hora».
     */
    sinHorario: z.boolean(),
}).strict();
/** `M-13` — un ajuste manual de `R-10`, con su motivo y su autor. */
export const esquemaAjusteManual = z.object({
    id: z.uuid(),
    cuando: z.iso.datetime(),
    /**
     * **Puede ser negativo**: `R-10` permite de −50 a +50, y restar es la mitad
     * del caso —se selló dos veces por error—. Un `nonnegative()` aquí habría
     * escondido justo los ajustes que más se quieren mirar.
     */
    sellos: z.number().int(),
    /** Obligatorio en el motor desde la 069: un ajuste sin porqué no entra. */
    motivo: z.string().min(1),
    /** Nulo si el ajuste no lo firma una persona del mostrador. */
    quien: z.string().min(1).nullable(),
    comensal: z.string().min(1),
    donde: z.string().min(1),
}).strict();
/** `R-09` — un premio entregado sin el código del comensal delante. */
export const esquemaCanjeSinVerificacion = z.object({
    id: z.uuid(),
    cuando: z.iso.datetime(),
    premio: z.string().min(1),
    quien: z.string().min(1).nullable(),
    comensal: z.string().min(1),
    donde: z.string().min(1),
}).strict();
/**
 * El cursor de la página siguiente, o nulo si no hay más.
 *
 * Por clave y no por número de página: con `offset`, un ajuste nuevo mientras
 * alguien pasa de página le **esconde una fila**, y en una lista de auditoría
 * eso es exactamente lo que no puede ocurrir.
 *
 * Nulo cuando la página vino incompleta — si el motor devolvió menos de lo que
 * cabía, no hay nada detrás.
 */
export const siguientePagina = (filas, limite) => {
    const ultima = filas[filas.length - 1];
    if (filas.length < limite || ultima === undefined)
        return null;
    return { antesDe: ultima.cuando, antesDeId: ultima.id };
};
/* ── `T-63` · El tablero — `M-01`, `M-02`, `M-04` y `M-08` ──────────────────*/
/**
 * Lo que el dueño ve al abrir el panel.
 *
 * ── Numerador y denominador, nunca la tasa ya hecha ──────────────────────
 * El motor devuelve los dos números de cada proporción y **no el porcentaje**, y
 * es la decisión que sostiene todo lo demás: una cafetería que abrió hace una
 * semana no tiene «0 % de retorno», **no tiene retorno todavía**. Con la tasa ya
 * calculada, ese caso llega como un cero que alguien va a usar para decidir si
 * el programa funciona.
 *
 * Teniendo los dos números, la pantalla puede decir «3 de 8» —que además es más
 * útil que un 37,5 %— y callar cuando el denominador es cero.
 */
export const esquemaTablero = z.object({
    /** `M-01` · Membresías vivas en el alcance pedido. */
    inscritos: z.number().int().nonnegative(),
    /** `M-01` · De ésas, cuántas nacieron dentro de la ventana. */
    inscritosNuevos: z.number().int().nonnegative(),
    /**
     * `M-04` · La métrica que dice si el programa sirve.
     *
     * `de` son quienes **ya tuvieron sus treinta días**, no todos los inscritos:
     * contar a quien se apuntó ayer como «no volvió» hunde la tasa con gente que
     * todavía está a tiempo.
     */
    retorno: z.object({
        de: z.number().int().nonnegative(),
        volvieron: z.number().int().nonnegative(),
    }).strict(),
    /** `M-08` · De los premios alcanzados en la ventana, cuántos se cobraron. */
    premios: z.object({
        alcanzados: z.number().int().nonnegative(),
        canjeados: z.number().int().nonnegative(),
    }).strict(),
}).strict();
/**
 * Una proporción, o **nulo si no hay de qué**.
 *
 * Nulo no es cero y la diferencia importa en las dos métricas donde se usa: «0 %
 * de retorno» dice que el programa no funciona; «todavía no se sabe» dice que
 * hay que esperar. Confundirlas cuesta una decisión de producto.
 */
export const proporcion = (cuantos, de) => de === 0 ? null : cuantos / de;
export const tasaDeRetorno = (t) => proporcion(t.retorno.volvieron, t.retorno.de);
export const tasaDeCanje = (t) => proporcion(t.premios.canjeados, t.premios.alcanzados);
/** `M-02` · Quién vuelve más, con su última visita. Sin teléfono, a propósito. */
export const esquemaClienteFrecuente = z.object({
    comensal: z.string().min(1),
    visitas: z.number().int().positive(),
    ultima: z.iso.datetime(),
}).strict();
/**
 * `T-64` · `M-06` — quién se está perdiendo la cafetería.
 *
 * La fila trae **la costumbre y la ausencia**, no un veredicto: «suele venir
 * cada 5 días y lleva 23». Quién es mucho lo decide el dueño, que conoce su
 * negocio; el motor sólo ordena por lo lejos que está cada quien de su propio
 * hábito.
 *
 * Los dos números son `numeric` en el motor y llegan con un decimal: un
 * intervalo de «cada 5,5 días» es una descripción y no una medida de precisión,
 * y redondearlo a entero haría que «cada 1 día» y «cada 1,4» se leyeran igual.
 */
export const esquemaClienteEnRiesgo = z.object({
    comensal: z.string().min(1),
    /** Tres o más: con menos no hay costumbre de la que echar de menos. */
    visitas: z.number().int().min(3),
    ultima: z.iso.datetime(),
    diasSinVenir: z.number().nonnegative(),
    intervaloDias: z.number().positive(),
    donde: z.string().min(1),
}).strict();
/**
 * Cuántas veces su costumbre lleva sin aparecer.
 *
 * Es lo que ordena la lista y lo que la hace comparable entre personas: quien
 * viene cada semana y lleva un mes (4,3×) está peor que quien viene a diario y
 * lleva cuatro días (4×), aunque el segundo tenga menos días encima.
 */
export const cuantasVecesSuCostumbre = (c) => c.diasSinVenir / c.intervaloDias;
/* ── `T-67` · El programa, que hasta hoy no lo escribía nadie ───────────────*/
/**
 * Lo que el dueño arma: cuántos sellos, qué se llevan, cuándo caduca y cuánto
 * le cuesta a él.
 *
 * ── Los dos nulos dicen cosas distintas, y ninguno es cero ───────────────
 * · `vigenciaDias` nulo es **«los sellos no vencen nunca»** — `R-03`. Un cero
 *   aquí sería «vencen hoy», que es lo contrario.
 * · `costoCentavos` nulo es **«no lo he puesto»**, y por eso `M-07` puede decir
 *   cuántos premios se deben sin decir cuánto valen. Un cero sería «el premio
 *   me sale gratis», y entonces el pasivo contable mentiría hacia abajo.
 *
 * Los límites son los mismos `check` de la 063 y la 075, escritos aquí otra vez
 * a propósito: el motor es el que manda, pero un 400 con el texto en español
 * llega antes y no gasta una transacción.
 */
export const esquemaPrograma = z.object({
    sellosPorPremio: z.number().int().min(1).max(100),
    premio: z.string().trim().min(1).max(120),
    vigenciaDias: z.number().int().min(1).max(3650).nullable(),
    costoCentavos: z.number().int().min(1).max(1_000_000).nullable(),
}).strict();
/**
 * Lo que se lee de la sección: el programa **o su ausencia**, y la bandera de
 * `R-08` con su fecha.
 *
 * `programa` nulo no es un error ni una cafetería rota: **toda cafetería nace
 * sin programa** —el alta de la consola no lo crea— y armarlo es el primer
 * trabajo del dueño. La pantalla lo distingue de «no se pudo cargar», que es lo
 * que haría si esto llegara como un objeto con ceros.
 */
export const esquemaProgramaDelPanel = z.object({
    programa: esquemaPrograma.nullable(),
    /** `R-08` · Si un sello de una sucursal ya vale en las demás. */
    compartido: z.boolean(),
    /**
     * Desde cuándo, para la frase «Encendido desde el 12 de agosto». Nulo cuando
     * no está encendido — y **sólo entonces**: la 077 escribe el eslabón en la
     * misma transacción que enciende la bandera, así que encendido sin fecha no
     * existe.
     */
    compartidoDesde: z.iso.datetime().nullable(),
}).strict();
/**
 * Cuántas visitas le faltan a quien lleva `sellos`, para la vista previa de la
 * tarjeta del cliente.
 *
 * Vive aquí y no en la pantalla porque es la misma cuenta que enseña el menú al
 * comensal, y dos restas iguales escritas en dos sitios se separan.
 */
export const visitasQueFaltan = (sellos, sellosPorPremio) => Math.max(0, sellosPorPremio - (sellos % sellosPorPremio));
/* ── `T-67` · `R-10` · El ajuste manual, desde el panel ────────────────────*/
/**
 * Una tarjeta encontrada por teléfono, para saber a quién se le ajusta.
 *
 * Una fila **por membresía**: sin `R-08`, la misma persona tiene una tarjeta en
 * cada sucursal donde ha comprado, y ajustar «sus sellos» sin decir cuáles
 * ajustaría los de la sucursal equivocada. Cuando hay una sola, la pantalla no
 * pregunta nada.
 */
export const esquemaTarjetaEncontrada = z.object({
    membresia: z.uuid(),
    comensal: z.string().min(1),
    sucursal: z.string().min(1),
    sellos: z.number().int().nonnegative(),
    /** Nulo mientras la cafetería no haya armado su programa. */
    sellosPorPremio: z.number().int().positive().nullable(),
    premios: z.number().int().nonnegative(),
}).strict();
/**
 * `R-10` · El ajuste: de −50 a +50, cero no, y el motivo obligatorio.
 *
 * Los tres límites son los de la 069 y se repiten aquí por lo mismo que los del
 * programa: el motor es el que manda, pero decirlo antes ahorra el viaje. El
 * motivo con tres caracteres mínimo no es un capricho — «ok» no es un motivo, y
 * `M-13` enseña esta columna al dueño meses después.
 */
export const esquemaAjuste = z.object({
    membresia: z.uuid(),
    sellos: z.number().int().min(-50).max(50).refine((n) => n !== 0, {
        message: 'Un ajuste de cero sellos no cambia nada',
    }),
    motivo: z.string().trim().min(3).max(300),
}).strict();
/** Cómo quedó: el saldo nuevo y si el ajuste pagó algún premio en el acto. */
export const esquemaAjusteHecho = z.object({
    saldo: z.number().int().nonnegative(),
    premiosNuevos: z.number().int().nonnegative(),
}).strict();
//# sourceMappingURL=lealtad.js.map